"use client"

import { motion } from "motion/react"

import Magnet from "@/core/ui/effects/Magnet"
import { ServiceTheme } from "@/core/utils/service-theme"

interface TechGravityProps {
  customTags?: string[]
  theme?: ServiceTheme
}

export default function TechGravity({ customTags, theme }: TechGravityProps) {
  const defaultTechnologies = [
    "React", "Next.js", "TypeScript", "Node.js", "Python",
    "Solidity", "Rust", "Three.js", "WebGL", "TailwindCSS",
    "PostgreSQL", "GraphQL", "Docker", "AWS", "Kubernetes",
    "Smart Contracts", "LLMs", "TensorFlow", "Framer Motion", "GSAP"
  ]

  const technologies = customTags || defaultTechnologies

  // Default colors if no theme provided
  const hoverBorder = theme ? theme.accentBorder : "group-hover:border-cyan-500/50"
  const bgDecor = theme ? theme.hex : "#6366f1" // Indigo-500

  return (
    <div className="py-24 bg-black relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          {/* If customTags are provided, we assume the parent handles the title or we can keep it generic */}
          {!customTags && (
            <>
              <motion.h2
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                className="text-3xl md:text-5xl font-bold text-white mb-6"
              >
                Core Technologies
              </motion.h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Modern, battle-tested frameworks and cloud infrastructure chosen for speed, reliability, and scale.
              </p>
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
          {technologies.map((tech, index) => (
            <Magnet key={tech} padding={50} disabled={false} magnetStrength={5}>
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                  delay: index * 0.05
                }}
                className="cursor-pointer group"
              >
                <div className={`px-6 py-3 rounded-full border border-white/10 bg-white/5 backdrop-blur-xs transition-all duration-300 group-hover:bg-white/15 ${hoverBorder}`}
                  style={theme ? { boxShadow: `0 0 30px -5px ${theme.hex}4d` } : undefined}
                >
                  <span className="text-sm md:text-base font-medium text-gray-300 group-hover:text-white transition-colors">
                    {tech}
                  </span>
                </div>
              </motion.div>
            </Magnet>
          ))}
        </div>
      </div>

      {/* Background Decor */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-[120px] pointer-events-none opacity-5"
        style={{ backgroundColor: bgDecor }}
      />
    </div>
  )
}
